import axios from "axios";
import { toast } from "sonner";
import i18n from "i18n";
import { API_URL } from "constants";

// Error code the backend answers with when a stored file turns out to be
// empty — kept in sync with erp-backend's files/upload controller. The key
// matches the i18n entry of the same name (dokobit/create's
// "document_file_empty" is read in utils/signer-edit-errors).
export const FILE_UPLOAD_EMPTY = "file_upload_empty";

// True when upload()/uploadWithFormKey() failed because the file was empty.
// The user already got a toast for it (see below), so a caller that would
// show its own error message for a failed upload skips it for this error.
export const isEmptyUploadError = (error) =>
  error?.response?.data?.message === FILE_UPLOAD_EMPTY;

// Shared by upload() and uploadWithFormKey(): a 0-byte file is rejected by
// the backend with FILE_UPLOAD_EMPTY. Toast it here once, for every caller,
// then rethrow so callers keep their own catch/loading-state flow.
const toastIfEmptyFileUpload = (error) => {
  if (isEmptyUploadError(error)) {
    toast.error(i18n.t(FILE_UPLOAD_EMPTY));
  }
  throw error;
};

// eslint-disable-next-line import/prefer-default-export
export const upload = (file, companyId) => {
  const url = `${API_URL}/${companyId}/files/upload`;
  const formData = new FormData();
  formData.append("file", file);
  return axios
    .post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .catch(toastIfEmptyFileUpload);
};

export const notToSaveUpload = (file, companyId) => {
  const url = `${API_URL}/${companyId}/files/upload/fuel-report`;
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadWithFormKey = (file, formKey) => {
  const url = `${API_URL}/customers/form-key/${formKey}/files/upload`;
  const formData = new FormData();
  formData.append("file", file);
  return axios
    .post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .catch(toastIfEmptyFileUpload);
};

export const fetchById = async (fileId, companyId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}`;
  return axios.get(url).then((response) => response.data);
};

export const fetchByIdAuthorizedFile = async (fileId, companyId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/access`;
  return axios.get(url).then((response) => response.data);
};

export const patchFile = async (data, companyId) => {
  const url = `${API_URL}/${companyId}/files/${data.id}/patch`;
  return axios.patch(url, data).then((response) => response.data);
};

/**
 * Attach a file under a parent (categories not needed) or detach it back to
 * top level (categories required — the file needs its own categories once it
 * is no longer inheriting the parent's). Errors come back as
 * res.notFound()/res.badRequest() with { message: <code> }; the codes match
 * i18n keys of the same name 1:1, so callers translate with t(error.response.data.message).
 * @param {string} companyId
 * @param {string} fileId
 * @param {{ parentId: string | null, categories?: string[] }} params
 */
export const setFileParent = async (companyId, fileId, { parentId, categories } = {}) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/parent`;
  return axios
    .patch(url, { parentId, categories })
    .then((response) => response.data);
};

/**
 * Main documents the file could be attached under (see setFileParent), for the
 * "Attach to document" picker. With an empty `q` (or one under two characters)
 * the backend suggests the newest documents of the same customer or category;
 * otherwise it searches the name, title and document number.
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} q
 * @returns {Promise<{ candidates: { id: string, name: string, date: string | null, categoryNames: string[], customerNames: string[] }[] }>}
 */
export const fetchParentCandidates = async (companyId, fileId, q = "") => {
  const url = `${API_URL}/${companyId}/files/${fileId}/parent-candidates`;
  return axios.get(url, { params: { q } }).then((response) => response.data);
};

export const fetchFileUrl = async (id, companyId, mode = "view") => {
  const url = `${API_URL}/${companyId}/files/download/${id}?mode=${mode}`;
  const response = await axios.get(url, { responseType: "blob" });
  const blob = new Blob([response.data], { type: "application/pdf" });
  const pdfUrl = URL.createObjectURL(blob);
  return pdfUrl;
};

export const uploadToDokobit = async (companyId, payload) => {
  const url = `${API_URL}/${companyId}/dokobit/create`;
  return axios.post(url, payload).then((response) => response.data);
};

export const replaceFile = async (companyId, payload) => {
  const url = `${API_URL}/${companyId}/files/manual-signed-document-upload`;
  return axios.post(url, payload).then((response) => response.data);
};

export const fetchCheckSumFiles = async (fileId, companyId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/checksum`;
  return axios.get(url).then((response) => response.data);
};

/**
 * @param {string} companyId
 * @param {string} fileId
 * @param {{ reason?: string }} [body] Optional — kept optional so old callers
 * that send nothing keep working. When the file is fully signed the backend
 * records an evidence snapshot and notifies signers/receivers before
 * resetting, using this reason if given.
 */
export const resetFileSignatures = async (companyId, fileId, body) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/reset-signatures`;
  return axios.post(url, body).then((response) => response.data);
};

/**
 * Master-only. Marks a FULLY signed document as annulled: the signed
 * original is kept (evidence) and swapped in the UI for a stamped
 * "ANULIUOTA" copy, signers/receivers are emailed, and linked leave
 * requests / business trips are cancelled. Errors come back as
 * res.badRequest()/res.notFound() with { message: <code> } — translate with
 * t(error.response.data.message).
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} reason Required, max 2000 chars.
 */
export const annulDocument = async (companyId, fileId, reason) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/annul`;
  return axios.post(url, { reason }).then((response) => response.data);
};

/**
 * Withdraws a template-generated document that is not yet fully signed — the
 * owner-mandated alternative to removing individual signature requests on
 * such a document (a signature/signature request may never be removed from a
 * document generated from a template; only the whole document can be pulled
 * back). Allowed for the file's owner or the company Master, and only while
 * the document is template-generated, unsigned, not annulled and not already
 * withdrawn — see files/withdraw.js. Errors come back as
 * res.badRequest()/res.forbidden()/res.notFound() with { message: <code> };
 * translate with t(error.response.data.message).
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} [reason] Optional.
 */
export const withdrawDocument = async (companyId, fileId, reason) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/withdraw`;
  return axios.post(url, { reason }).then((response) => response.data);
};

/**
 * Annulment/resign history for a file: `annulments` (rows raised on this
 * file, newest first) and `replaces` (rows of OTHER annulled files that
 * point their replacement at this one). `recipients` on each annulment row
 * is included only when the requester can manage (Master) — see
 * files/annulments/fetch.js.
 * @param {string} companyId
 * @param {string} fileId
 */
export const fetchDocumentAnnulments = async (companyId, fileId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/annulments`;
  return axios.get(url).then((response) => response.data);
};

/**
 * Master-only. Points (or clears, with `replacementFileId: null`) an
 * `annulled` row's "replaced by" link — shown only in the document card UI,
 * never written into the PDF itself.
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} annulmentId
 * @param {string | null} replacementFileId
 */
export const setAnnulmentReplacement = async (
  companyId,
  fileId,
  annulmentId,
  replacementFileId
) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/annulments/${annulmentId}/replacement`;
  return axios
    .patch(url, { replacementFileId })
    .then((response) => response.data);
};

/**
 * Master-only. Re-sends the annulment/resign notice to recipients that have
 * no successful email yet. Returns the refreshed recipients list.
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} annulmentId
 */
export const resendAnnulmentNotices = async (
  companyId,
  fileId,
  annulmentId
) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/annulments/${annulmentId}/resend`;
  return axios.post(url).then((response) => response.data);
};

/**
 * Master-only. Downloads the signed original PDF captured at annulment time
 * (the bytes that were replaced by the stamped "ANULIUOTA" copy).
 * @param {string} companyId
 * @param {string} fileId
 * @param {string} annulmentId
 * @param {string} [fileName] Save-as name; falls back to a generic one since
 * the blob URL swap below drops the server's Content-Disposition filename.
 */
export const downloadAnnulmentOriginal = async (
  companyId,
  fileId,
  annulmentId,
  fileName
) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/annulments/${annulmentId}/original`;
  const response = await axios.get(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "signed-original.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};
